// Section scaffolding for the report summary: a labelled card wrapper plus
// the two notices that keep the summary's three non-happy states distinct.
//
// The distinction is the point. A section whose read FAILED renders the error
// notice ("couldn't be loaded"); a section that read fine and genuinely has
// no records renders "Nothing to report". Collapsing the two would let a
// missing number read as a zero, which on a management report is a false
// statement rather than a cosmetic bug.

import type { ReactNode } from "react";
import Card from "../../common/Card";
import Alert from "../../common/Alert";

export interface ReportSectionProps {
  /** Slug used to build the heading id the section is labelled by. */
  id: string;
  title: string;
  /** Optional control rendered on the heading row (e.g. an edit affordance). */
  action?: ReactNode;
  children: ReactNode;
}

export default function ReportSection({
  id,
  title,
  action,
  children,
}: ReportSectionProps) {
  const headingId = `report-${id}-heading`;
  return (
    <Card padding="md">
      <section aria-labelledby={headingId}>
        <div className="flex items-start justify-between gap-3">
          {/* Level 2 under the page's h1. The size class is chosen for the
              visual hierarchy and is deliberately independent of the level —
              jumping h1 to h3 would leave a gap in the document outline. */}
          <h2
            id={headingId}
            className="text-h4 text-slate-900 dark:text-slate-50"
          >
            {title}
          </h2>
          {action}
        </div>
        <div className="mt-4">{children}</div>
      </section>
    </Card>
  );
}

/** Rendered in place of a section whose underlying read failed. */
export function UnavailableNotice({ label }: { label: string }) {
  return (
    <Alert variant="error">
      {label} couldn&apos;t be loaded. Refresh to try again.
    </Alert>
  );
}

/** Rendered for a section that read successfully and has no records. */
export function NothingToReport() {
  return (
    <p className="text-body-sm text-slate-500 dark:text-slate-400">
      Nothing to report
    </p>
  );
}
